/**
 * Teamwork state next to the inbox store: who is in which chat (presence), internal notes, customer tags,
 * the follow-up card, the "someone just replied" guard and the note mode of the composer area.
 * The inbox store calls in at a few points (select, setText, send, init/dispose); everything else is here.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '../api/client'
import { getCollabApi } from '../api/collab'
import type { CollabEvent, FollowUpInfo, Note, TagDef } from '../api/collab/types'
import type { RealtimeConnection } from '../api/realtime'
import type { TagTone } from '../api/types'
import {
  applyPresence,
  othersIn,
  pruneExpired,
  recentOtherReply,
  type Collision,
  type PresenceEntry,
  type PresenceMap,
} from '../lib/collab'
import { newClientId } from '../lib/send'
import { useClock } from './clock'
import { useInbox } from './inbox'
import { useLibrary } from './library'

export const HEARTBEAT_MS = 5000
export const TYPING_EVERY_MS = 2500

export const useCollab = defineStore('collab', () => {
  const clock = useClock()
  const api = () => getCollabApi()

  // ------------------------------------------------ state
  const presence = ref<PresenceMap>({})
  const notes = ref<Record<string, Note[]>>({})
  const notesLoaded = ref<Record<string, boolean>>({})
  const tagDefs = ref<TagDef[]>([])
  const canManageTags = ref(false)
  const followUps = ref<Record<string, FollowUpInfo | null>>({})
  const followLoading = ref<Record<string, boolean>>({})
  const noteMode = ref<Record<string, boolean>>({})
  /** The send that was held back because someone else just replied. */
  const collision = ref<{ convId: string; info: Collision } | null>(null)
  const busy = ref(false)

  let conn: RealtimeConnection | null = null
  let viewingId: string | null = null
  let beat: ReturnType<typeof setInterval> | null = null
  const lastTyping: Record<string, number> = {}
  let visHooked = false

  const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))
  const flash = (msg: string) => useInbox().flash(msg)

  // ------------------------------------------------ presence
  function send(msg: unknown): void {
    conn?.send?.(msg)
  }

  function presenceMsg(id: string, state: 'viewing' | 'typing' | 'left'): void {
    send({ type: 'presence', conversation_id: id, state })
  }

  const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

  function startBeat(): void {
    stopBeat()
    beat = setInterval(() => {
      if (viewingId && visible()) presenceMsg(viewingId, 'viewing')
      pruneExpired(presence.value, clock.now)
    }, HEARTBEAT_MS)
  }

  function stopBeat(): void {
    if (beat) clearInterval(beat)
    beat = null
  }

  function hookVisibility(): void {
    if (visHooked || typeof document === 'undefined') return
    visHooked = true
    document.addEventListener('visibilitychange', () => {
      if (!viewingId) return
      presenceMsg(viewingId, visible() ? 'viewing' : 'left')
    })
  }

  /** The screen now shows this chat (or none). */
  function viewing(id: string | null): void {
    if (viewingId === id) return
    if (viewingId) presenceMsg(viewingId, 'left')
    viewingId = id
    collision.value = null
    if (id && visible()) presenceMsg(id, 'viewing')
  }

  /** The staff typed in the composer (called from the inbox store's setText; throttled). */
  function typing(id: string): void {
    const now = Date.now()
    if (now - (lastTyping[id] ?? 0) < TYPING_EVERY_MS) return
    lastTyping[id] = now
    presenceMsg(id, 'typing')
  }

  function attach(c: RealtimeConnection): void {
    conn = c
    hookVisibility()
    startBeat()
    if (viewingId) presenceMsg(viewingId, 'viewing')
  }

  function detach(): void {
    if (viewingId) presenceMsg(viewingId, 'left')
    stopBeat()
    conn = null
  }

  /** Colleagues in a chat now (never me). Re-evaluated every clock tick, so entries expire on screen. */
  function others(convId: string): PresenceEntry[] {
    return othersIn(presence.value, convId, useInbox().meId, clock.now)
  }

  // ------------------------------------------------ realtime events
  function onEvent(e: CollabEvent): void {
    const now = clock.now
    switch (e.type) {
      case 'presence':
        applyPresence(presence.value, e, now)
        return
      case 'presence.snapshot':
        for (const item of e.items) applyPresence(presence.value, item, now)
        return
      case 'note.changed': {
        // reload the notes of every loaded chat of that customer (or of that chat for a group)
        for (const [convId, list] of Object.entries(notes.value)) {
          const same =
            convId === e.conversation_id || (e.contact_id !== null && list.some((n) => n.contact_id === e.contact_id))
          const contact = useInbox().details[convId]?.contact.id ?? null
          if (same || (e.contact_id !== null && contact === e.contact_id)) void loadNotes(convId)
        }
        return
      }
      case 'tags.changed':
        void loadTags()
        return
      case 'settings.changed':
        void useInbox().loadTeam().catch(() => undefined)
        return
    }
  }

  // ------------------------------------------------ notes
  async function loadNotes(convId: string): Promise<void> {
    try {
      notes.value[convId] = await api().listNotes(convId)
      notesLoaded.value[convId] = true
    } catch {
      /* not critical: the chat works without notes; the next open retries */
    }
  }

  function notesFor(convId: string): Note[] {
    return notes.value[convId] ?? []
  }

  async function addNote(convId: string, text: string): Promise<boolean> {
    const t = text.trim()
    if (!t) return false
    busy.value = true
    try {
      const n = await api().addNote(convId, t)
      const list = notes.value[convId] ?? (notes.value[convId] = [])
      if (!list.some((x) => x.id === n.id)) list.unshift(n)
      return true
    } catch (e) {
      flash(`บันทึกโน้ตไม่สำเร็จ: ${errText(e)}`)
      return false
    } finally {
      busy.value = false
    }
  }

  async function editNote(convId: string, note: Note, text: string): Promise<boolean> {
    try {
      const n = await api().editNote(note.id, text)
      const list = notes.value[convId] ?? []
      const i = list.findIndex((x) => x.id === n.id)
      if (i >= 0) list.splice(i, 1, n)
      return true
    } catch (e) {
      flash(`แก้โน้ตไม่สำเร็จ: ${errText(e)}`)
      return false
    }
  }

  async function deleteNote(convId: string, note: Note): Promise<void> {
    const list = notes.value[convId] ?? []
    const i = list.findIndex((x) => x.id === note.id)
    if (i >= 0) list.splice(i, 1)
    try {
      await api().deleteNote(note.id)
    } catch (e) {
      if (i >= 0) list.splice(i, 0, note)
      flash(`ลบโน้ตไม่สำเร็จ: ${errText(e)}`)
    }
  }

  function setNoteMode(convId: string, on: boolean): void {
    noteMode.value[convId] = on
  }

  // ------------------------------------------------ tags
  async function loadTags(): Promise<void> {
    try {
      const res = await api().listTags()
      tagDefs.value = res.items
      canManageTags.value = res.can_manage
    } catch {
      /* the list works without labels */
    }
  }

  const tagById = computed(() => new Map(tagDefs.value.map((t) => [t.id, t])))

  async function setTags(convId: string, ids: string[]): Promise<void> {
    const inbox = useInbox()
    const before = inbox.convs[convId]
    try {
      const res = await api().setConversationTags(convId, ids)
      inbox.applyEvent({ type: 'conversation.updated', conversation: res.conversation })
      const d = inbox.details[convId]
      if (d) {
        d.tags = res.conversation.tags ?? []
        d.tag = res.conversation.tag
      }
      void loadTags() // counts
    } catch (e) {
      if (before) inbox.applyEvent({ type: 'conversation.updated', conversation: before })
      flash(`ติดแท็กไม่สำเร็จ: ${errText(e)}`)
    }
  }

  async function createTag(name: string, tone: TagTone): Promise<TagDef | null> {
    try {
      const t = await api().createTag(name, tone)
      await loadTags()
      return t
    } catch (e) {
      flash(`เพิ่มแท็กไม่สำเร็จ: ${errText(e)}`)
      return null
    }
  }

  // ------------------------------------------------ follow-up
  /** Per chat: the newest card read (an older answer landing later, or one from before a send, is dropped). */
  const followSeq: Record<string, number> = {}

  /** Read the follow-up card (again: realtime changed the chat's follow-up, the send window ran out, a reconnect). */
  async function loadFollowUp(convId: string): Promise<void> {
    const n = (followSeq[convId] = (followSeq[convId] ?? 0) + 1)
    followLoading.value[convId] = true
    try {
      const info = await api().getFollowUp(convId)
      if (n === followSeq[convId]) followUps.value[convId] = info
    } catch (e) {
      if (n !== followSeq[convId]) return
      followUps.value[convId] = null
      if (!(e instanceof ApiError && e.status === 404)) flash(`โหลดข้อความทักตามไม่สำเร็จ: ${errText(e)}`)
    } finally {
      if (n === followSeq[convId]) followLoading.value[convId] = false
    }
  }

  /** The card is done (sent / skipped): a read still on its way must not bring the old card back. */
  function followDone(convId: string): void {
    followSeq[convId] = (followSeq[convId] ?? 0) + 1
    followLoading.value[convId] = false
    followUps.value[convId] = null
  }

  async function sendFollowUp(convId: string, text: string, source: 'canned' | 'ai' | 'template' | 'manual',
                              cannedId: string | null): Promise<boolean> {
    const t = text.trim()
    if (!t) return false
    const inbox = useInbox()
    busy.value = true
    try {
      const res = await api().sendFollowUp(convId, { text: t, client_id: newClientId(), source, canned_id: cannedId })
      inbox.applyEvent({ type: 'message.created', conversation_id: convId, item: res.message, conversation: res.conversation })
      void inbox.refreshThread(convId).catch(() => undefined) // the "ทักตามแล้ว" line
      followDone(convId)
      return true
    } catch (e) {
      flash(`ส่งข้อความทักตามไม่สำเร็จ: ${errText(e)}`)
      if (e instanceof ApiError && (e.status === 409 || e.status === 422)) void loadFollowUp(convId)
      return false
    } finally {
      busy.value = false
    }
  }

  async function skipFollowUp(convId: string): Promise<void> {
    const inbox = useInbox()
    busy.value = true
    try {
      const res = await api().skipFollowUp(convId)
      inbox.applyEvent({ type: 'conversation.updated', conversation: res.conversation })
      void inbox.refreshThread(convId).catch(() => undefined)
      followDone(convId)
    } catch (e) {
      flash(`ข้ามการทักตามไม่สำเร็จ: ${errText(e)}`)
    } finally {
      busy.value = false
    }
  }

  async function setAuto(convId: string, enabled: boolean): Promise<void> {
    const f = followUps.value[convId]
    if (f) f.auto.enabled = enabled
    try {
      await api().setFollowUpAuto(convId, enabled)
    } catch (e) {
      if (f) f.auto.enabled = !enabled
      flash(`ตั้งทักตามอัตโนมัติไม่สำเร็จ: ${errText(e)}`)
    }
  }

  // ------------------------------------------------ collision guard
  function collisionFor(convId: string): Collision | null {
    const inbox = useInbox()
    return recentOtherReply(inbox.threads[convId] ?? [], inbox.meId, clock.now)
  }

  function askCollision(convId: string, info: Collision): void {
    collision.value = { convId, info }
  }

  function dismissCollision(): void {
    collision.value = null
  }

  async function confirmCollision(): Promise<void> {
    const c = collision.value
    collision.value = null
    if (!c) return
    // the pictures staged in the composer go too (the held send put them back), like the composer's own send
    const { assets, restore } = useLibrary().takeStaged(c.convId)
    await useInbox().send(c.convId, { force: true, media: assets, onRefused: restore })
  }

  async function init(): Promise<void> {
    await loadTags()
  }

  return {
    presence,
    notes,
    notesLoaded,
    tagDefs,
    canManageTags,
    tagById,
    followUps,
    followLoading,
    noteMode,
    collision,
    busy,
    init,
    attach,
    detach,
    viewing,
    typing,
    others,
    onEvent,
    loadNotes,
    notesFor,
    addNote,
    editNote,
    deleteNote,
    setNoteMode,
    loadTags,
    setTags,
    createTag,
    loadFollowUp,
    sendFollowUp,
    skipFollowUp,
    setAuto,
    collisionFor,
    askCollision,
    dismissCollision,
    confirmCollision,
  }
})
